import React, { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { Globe, Loader2, LogIn } from "lucide-react";
import { useLang } from "../i18n";
import { useAuth, homeFor } from "../api";
import { ASSETS } from "../assets";

const webglOk = () => {
  try {
    const c = document.createElement("canvas");
    return !!(window.WebGLRenderingContext && (c.getContext("webgl") || c.getContext("experimental-webgl")));
  } catch { return false; }
};

const Particles = ({ count = 360 }) => {
  const ref = useRef();
  const geo = useMemo(() => {
    const pos = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 14;
      pos[i * 3 + 1] = Math.random() * 8 - 1;
      pos[i * 3 + 2] = (Math.random() - 0.5) * 8;
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    return g;
  }, [count]);
  useFrame(({ clock }) => {
    if (!ref.current) return;
    const p = ref.current.geometry.attributes.position;
    const t = clock.getElapsedTime();
    for (let i = 0; i < count; i++) {
      p.array[i * 3 + 1] += 0.006 + (i % 5) * 0.001;
      if (p.array[i * 3 + 1] > 7) p.array[i * 3 + 1] = -1;
      p.array[i * 3] += Math.sin(t + i) * 0.0015;
    }
    p.needsUpdate = true;
    ref.current.rotation.y = t * 0.03;
  });
  return (
    <points ref={ref} geometry={geo}>
      <pointsMaterial size={0.07} color="#7fd4ff" transparent opacity={0.85} sizeAttenuation />
    </points>
  );
};

const Scene = () => (
  <Canvas camera={{ position: [0, 1.6, 6], fov: 45 }} dpr={[1, 1.8]} gl={{ antialias: true, alpha: true }}>
    <ambientLight intensity={0.8} />
    <pointLight position={[-4, 2, 3]} intensity={40} color="#3fa9ff" distance={16} />
    <Suspense fallback={null}><Particles /></Suspense>
  </Canvas>
);

export default function Login() {
  const { t, lang, setLang } = useLang();
  const { login } = useAuth();
  const navigate = useNavigate();

  // the login screen should never be a search result — only the landing page is
  useEffect(() => {
    const tag = document.querySelector('meta[name="robots"]');
    const prevRobots = tag?.content;
    if (tag) tag.content = "noindex, nofollow";
    const prev = document.title;
    document.title = "تسجيل الدخول | إنجلش لاب";
    return () => {
      if (tag && prevRobots) tag.content = prevRobots;
      document.title = prev;
    };
  }, []);
  const [form, setForm] = useState({ username: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const gl = useMemo(webglOk, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const u = await login(form.username.trim(), form.password);
      navigate(homeFor(u.role));
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen relative overflow-x-hidden" style={{ background: "#061029" }}>
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(1000px 600px at 75% -5%, rgba(63,169,255,.4), transparent 62%), radial-gradient(800px 500px at 5% 100%, rgba(122,92,240,.35), transparent 62%)" }} />
      <div className="absolute inset-0 opacity-80 pointer-events-none">{gl ? <Scene /> : null}</div>

      <button data-testid="lang-toggle" onClick={() => setLang(lang === "ar" ? "en" : "ar")}
        className="absolute top-4 end-4 z-30 rounded-full bg-white/15 border border-white/25 backdrop-blur-md
          text-white px-4 py-2 text-sm font-bold flex items-center gap-2">
        <Globe size={15} />{lang === "ar" ? "English" : "العربية"}
      </button>

      <div className="relative z-20 min-h-screen max-w-6xl mx-auto px-5 sm:px-8 py-16 lg:py-10
        grid lg:grid-cols-2 gap-8 items-center">
        {/* art column */}
        <div className="hidden lg:flex relative items-center justify-center order-1">
          <img src={ASSETS.island} alt="" aria-hidden
            className="w-full max-w-[34rem] float-slow mix-blend-screen soft-mask-wide pointer-events-none" />
          <img src={ASSETS.mascot} alt="" data-testid="login-mascot"
            className="absolute -bottom-6 start-0 w-[11rem] xl:w-[13rem] float-slow
              mix-blend-screen soft-mask pointer-events-none" />
        </div>

        {/* form column */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 90, damping: 16 }}
          className="w-full max-w-md mx-auto glass p-6 sm:p-8 order-2">
          <img src={ASSETS.logo} alt="English Lab" data-testid="login-logo"
            className="w-full max-w-[15rem] mx-auto mix-blend-screen soft-mask-wide" />
          <p className="text-white/70 mt-1 text-base text-center">{t("tagline")}</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="text-xs font-bold text-white/60 uppercase tracking-widest">{t("username")}</label>
              <input data-testid="login-username" className="input mt-1" autoComplete="username"
                value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} required />
            </div>
            <div>
              <label className="text-xs font-bold text-white/60 uppercase tracking-widest">{t("password")}</label>
              <input data-testid="login-password" type="password" className="input mt-1"
                autoComplete="current-password" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} required />
            </div>
            {err && <div data-testid="login-error" className="text-sm font-bold rounded-xl px-3 py-2"
              style={{ background: "rgba(232,56,79,.18)", color: "#ffb3bf" }}>{err}</div>}
            <button data-testid="login-submit" type="submit" disabled={busy}
              className="btn3d w-full flex items-center justify-center gap-2"
              style={{ background: "linear-gradient(180deg,#ffb43f,#ef8a00)" }}>
              {busy ? <Loader2 className="animate-spin" size={18} /> : <LogIn size={18} />}
              {t("signIn")}
            </button>
          </form>
          <p className="text-xs text-white/50 mt-4 text-center">{t("loginHint")}</p>
          <Link to="/" data-testid="login-back-home"
            className="block text-xs text-white/60 hover:text-white transition-colors mt-4 text-center">
            {lang === "ar" ? "→ عودة للصفحة الرئيسية" : "← Back to home page"}</Link>
        </motion.div>
      </div>
    </div>
  );
}
